/**
 * The one place the dashboard talks to the API.
 *
 * `credentials: 'include'` on every call, because the session is a cookie on a
 * different origin — `app.{primary}` calling `api.{primary}` (ARCH §4.1). CORS
 * is scoped to exactly this origin in `lib/http.ts`, never a wildcard, since a
 * wildcard cannot carry credentials at all.
 *
 * Plain `fetch` rather than `hono/client`. ARCH §4.1 makes `/v1/**` Hono RPC for
 * the dashboard, and it will be — but the auth surface is almost entirely
 * Better Auth's own handler behind a catch-all mount, which no RPC type can
 * describe. Typing one route while four neighbours stay untyped buys nothing.
 * The RPC client arrives with the app shell (Story 3.8), where the first
 * genuinely Leadgible-owned `/v1/w/{wid}/**` routes are.
 *
 * Story 3.3.
 */

/** The one error shape, ARCH §4.3 — what every failure from the API looks like. */
export type ApiError = {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id: string
}

export class ApiFailure extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown> | undefined
  readonly requestId: string

  constructor(status: number, error: ApiError) {
    // `message` NAMES THE FIX (ARCH §4.3), so it is what a screen renders.
    // There is deliberately no client-side rewording: a message the UI invents
    // is a message that can disagree with the server's.
    super(error.message)
    this.name = 'ApiFailure'
    this.status = status
    this.code = error.code
    this.details = error.details
    this.requestId = error.request_id
  }
}

/**
 * Where the API lives.
 *
 * A build-time var per environment, like `APP_ORIGIN` is on the other side.
 * Falls back to the staging API so `vite preview` and the screenshot pass work
 * with no environment at all — the screens render their own states without a
 * network round trip anyway.
 */
const API_ORIGIN =
  (import.meta.env['VITE_API_ORIGIN'] as string | undefined) ??
  'https://leadgible-staging-api.yhengdesigns.workers.dev'

async function request<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_ORIGIN}${path}`, {
      credentials: 'include',
      ...init,
    })
  } catch {
    // The network, not the API. Named as such rather than dressed up as a
    // server error, because the fix is different: try again, check the
    // connection.
    throw new ApiFailure(0, {
      code: 'network_unavailable',
      message: "We couldn't reach Leadgible. Check your connection and try again.",
      request_id: 'none',
    })
  }

  if (response.ok) return (await response.json()) as T

  let parsed: { error?: ApiError } = {}
  try {
    parsed = (await response.json()) as { error?: ApiError }
  } catch {
    // A non-JSON error body is a surprise; it must not become a blank screen.
  }
  throw new ApiFailure(
    response.status,
    parsed.error ?? {
      code: 'internal_error',
      message: 'Something went wrong on our end. Try again in a moment.',
      request_id: 'none',
    },
  )
}

/**
 * `signal` lets a caller give up on a call that is taking too long — Story 27.4's draft, which a
 * model takes 12 to 32 s to answer and which must never leave a wait that does not end. An
 * aborted call throws as the network failing does, which is what it is to the person waiting.
 */
export async function apiPost<T>(path: string, body: unknown, options: { signal?: AbortSignal } = {}): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  })
}

/** Story 3.8: the shell's boot call, and every read after it. */
export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' })
}

/** Story 4.1: a partial update — the role change on a member. */
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/**
 * Story 4.1: removing a member, revoking an invitation.
 *
 * A DELETE MAY CARRY A BODY, and Story 4.4's needs one — turning two-factor off
 * requires a password AND a code, and neither belongs in a URL where it would
 * reach a proxy log. The body is optional so every existing call is unchanged.
 */
export async function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'DELETE',
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  })
}

/** Story 4.2: a full replacement — the workspace access list. */
export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}
